import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { opened1 } from "../../lib/beacon";
import { getStarters, sendFeedback, streamChat, loadConversation, getConvId, setConvId, doorReady, UNAVAILABLE } from "../../lib/chat";

const NOT_SET = "Chat is not set up yet";

// fold a stream event (or a stored message) into one answer
const addEvent = (m, e) => {
  if (e.type === "text") return { ...m, text: m.text + (e.text || "") };
  if (e.type === "cite") return { ...m, cites: [...m.cites, e] };
  if (e.type === "press") return { ...m, presses: [...m.presses, e] };
  if (e.type === "draft") return { ...m, drafts: [...m.drafts, e] };
  if (e.type === "limit" || e.type === "error") return { ...m, notes: [...m.notes, e.message] };
  return m;
};
function Rate({ conv, index, onFail }) {
  const [vote, setVote] = useState("");
  const [why, setWhy] = useState("");
  const [done, setDone] = useState(false);
  const post = async (v, reason) => {
    try {
      await sendFeedback({ conversation_id: conv, index, vote: v, ...(reason ? { reason } : {}) });
      return true;
    } catch (e) { onFail(e); return false; }
  };
  const tapVote = async (v) => {
    if (vote) return;
    setVote(v);
    if (await post(v) && v === "up") setDone(true);
  };
  if (done) return <div className="vc-rate faint" data-testid={`chat-thanks-${index}`}>Thanks</div>;
  return (
    <div className="vc-rate" data-testid={`chat-rate-${index}`}>
      <button className={`vc-vote${vote === "up" ? " on" : ""}`} aria-label="Good answer" data-testid={`chat-up-${index}`} disabled={Boolean(vote)} onClick={() => tapVote("up")}>👍</button>
      <button className={`vc-vote${vote === "down" ? " on" : ""}`} aria-label="Poor answer" data-testid={`chat-down-${index}`} disabled={Boolean(vote)} onClick={() => tapVote("down")}>👎</button>
      {vote === "down" && (
        <form className="vc-why" onSubmit={async (e) => { e.preventDefault(); if (await post("down", why.trim())) setDone(true); }}>
          <input className="vk-input" maxLength={200} value={why} onChange={(e) => setWhy(e.target.value)} placeholder="What was wrong?" aria-label="What was wrong?" data-testid={`chat-why-${index}`} />
          <button className="btn tiny" type="submit" data-testid={`chat-why-send-${index}`}>Send</button>
        </form>
      )}
    </div>
  );
}

const blank = (role, text = "") => ({ role, text, cites: [], presses: [], drafts: [], notes: [] });

function Draft({ d, i }) {
  const copy = () => navigator.clipboard?.writeText(d.body || "").then(() => toast("Copied")).catch(() => toast("Copy failed"));
  return (
    <div className="vc-draft" data-testid={`chat-draft-${i}`}>
      <div className="vc-draft-h"><span className="vc-draft-tag">Draft — not sent</span>{d.title && <span>{d.title}</span>}</div>
      <pre>{d.body}</pre>
      <button className="btn ghost tiny" data-testid={`chat-draft-copy-${i}`} onClick={copy}>Copy</button>
    </div>
  );
}

function Answer({ m, idx, onCite, onPress, conv, onFail }) {
  return (
    <div className={`vc-msg ${m.role === "person" ? "me" : "vk"}`} data-testid={`chat-msg-${idx}`}>
      {m.text && <div className="vc-text">{m.text}</div>}
      {m.notes.map((n, i) => <div key={i} className="vc-note" data-testid={`chat-note-${idx}-${i}`}>{n}</div>)}
      {m.cites.length > 0 && (
        <div className="vc-cites" data-testid={`chat-cites-${idx}`}>
          <span className="faint">Source</span>
          {m.cites.map((c, i) => (c.kind === "card"
            ? <button key={i} className="vc-cite" data-testid={`chat-cite-${c.id}`} onClick={() => onCite(c.id)}>{c.id}</button>
            : <span key={i} className="vc-cite doc" title={c.title}>{c.title || c.id}</span>))}
        </div>
      )}
      {m.presses.map((p, i) => (
        <button key={i} className="btn tiny vc-press" data-testid={`chat-press-${idx}-${i}`} onClick={() => onPress(p)}>
          {p.label} · {p.card_id}
        </button>
      ))}
      {m.drafts.map((d, i) => <Draft key={i} d={d} i={`${idx}-${i}`} />)}
      {m.role !== "person" && m.done && <Rate conv={m.done.conversation_id || conv} index={m.done.index} onFail={onFail} />}
    </div>
  );
}

export function VikasChat({ open, cardId, onClose, onCite, onPress, clearCard }) {
  const [ready, setReady] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [conv, setConv] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [reload, setReload] = useState(false);
  const [starters, setStarters] = useState([]);
  const fail = (e) => { setErr(e?.message || UNAVAILABLE); if (e?.status === 401) setReload(true); };
  const endRef = useRef(null);
  const inRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    opened1("chat");
    let live = true;
    (async () => {
      setErr("");
      const ok = await doorReady();
      if (!live) return;
      setReady(ok);
      if (!ok) return;
      getStarters().then((s) => { if (live) setStarters(s); }).catch(() => {});
      try {
        const id = await getConvId();
        if (!live) return;
        setConv(id);
        if (id) {
          const past = await loadConversation(id);
          if (live && past) setMsgs(past.map((m) => ({ ...blank(m.role, m.text || ""), cites: m.cites || [], drafts: m.drafts || [] })));
        }
      } catch (e) { if (live) fail(e); }
    })();
    inRef.current?.focus();
    return () => { live = false; };
  }, [open]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  if (!open) return null;

  const send = async (pick) => {
    const q = (typeof pick === "string" ? pick : text).trim();
    if (!q || busy) return;
    if (typeof pick !== "string") setText("");
    setErr(""); setBusy(true);
    setMsgs((x) => [...x, blank("person", q), blank("vikas")]);
    const patch = (e) => setMsgs((x) => [...x.slice(0, -1), addEvent(x[x.length - 1], e)]);
    try {
      const body = { message: q };
      if (cardId) body.card_id = cardId;
      if (conv) body.conversation_id = conv;
      const { out, status } = await streamChat(body, (e) => {
        if (e.type === "done") {
          if (Number.isInteger(e.index)) setMsgs((x) => [...x.slice(0, -1), { ...x[x.length - 1], done: { index: e.index, conversation_id: e.conversation_id || conv } }]);
          if (e.conversation_id && e.conversation_id !== conv) { setConv(e.conversation_id); setConvId(e.conversation_id).catch(() => {}); }
        } else patch(e);
      });
      if (out === "not-ready") setReady(false);
      if (status === 401) setReload(true);
    } catch (e) { fail(e); setMsgs((x) => (x[x.length - 1].text ? x : x.slice(0, -1))); }
    setBusy(false);
  };
  const fresh = async () => {
    try { await setConvId(""); setConv(""); setMsgs([]); setErr(""); } catch { setErr(UNAVAILABLE); }
  };

  return (
    <div className="vchat" role="dialog" aria-label="VIKAS chat" data-testid="chat-drawer">
      <div className="vc-head">
        <span className="vc-title">VIKAS</span>
        {ready && <button className="btn ghost tiny" data-testid="chat-new" onClick={fresh}>New chat</button>}
        <button className="vc-x" aria-label="Close chat" data-testid="chat-close" onClick={onClose}><X size={18} /></button>
      </div>
      {ready === false ? <div className="vc-empty" data-testid="chat-not-set">{NOT_SET}</div> : (
        <>
          <div className="vc-body" data-testid="chat-body">
            {msgs.length === 0 && !err && <div className="vc-empty faint">Ask about the cards you can see. Drafts are never sent.</div>}
            {msgs.length === 0 && starters.length > 0 && (
              <div className="vc-starters" data-testid="chat-starters">
                {starters.map((s, i) => <button key={i} className="vc-starter" data-testid={`chat-starter-${i}`} disabled={busy} onClick={() => send(s)}>{s}</button>)}
              </div>
            )}
            {msgs.map((m, i) => <Answer key={i} m={m} idx={i} onCite={onCite} onPress={onPress} conv={conv} onFail={fail} />)}
            {err && <div className="vc-note" data-testid="chat-error">{err}</div>}
            {reload && <button className="btn tiny" data-testid="chat-reload" onClick={() => window.location.reload()}>Reload</button>}
            <div ref={endRef} />
          </div>
          {cardId && (
            <div className="vc-ctx" data-testid="chat-card-context">
              About card <b>{cardId}</b>
              <button className="vc-x small" aria-label="Clear card" data-testid="chat-card-clear" onClick={clearCard}><X size={14} /></button>
            </div>
          )}
          <form className="vc-in" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <textarea ref={inRef} rows={2} value={text} placeholder="Ask VIKAS" data-testid="chat-input"
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
            <button className="btn" type="submit" disabled={busy || !text.trim() || ready !== true} data-testid="chat-send">{busy ? "…" : "Send"}</button>
          </form>
        </>
      )}
    </div>
  );
}
